import { useEffect, useState } from "react";
import { X, Upload, Loader2, MapPin, ShieldCheck } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { PROPERTY_TYPES, FACADES, VIEWS, FLOORS, STATUS_LABEL } from "@/lib/portfolioOptions";
import { LocationPicker } from "@/components/LocationPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = {
  title: "", listing_type: "SALE", property_type: "2+1", rooms: "2+1", price: "", area_m2: "",
  floor: "", total_floors: "", facades: [], views: [], has_eids: false, eids_code: "",
  lat: null, lng: null, city: "", district: "", neighborhood: "", description: "", status: "ACTIVE", image_paths: [], parcel: null,
};

const Chips = ({ options, value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {Object.entries(options).map(([k, l]) => {
      const on = value.includes(k);
      return (
        <button key={k} type="button" data-testid={`${testPrefix}-${k}`} onClick={() => onChange(on ? value.filter((x) => x !== k) : [...value, k])}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
          {l}
        </button>
      );
    })}
  </div>
);

export const PortfolioForm = ({ open, editing, onClose, onSaved, initial, createUrl = "/portfolios" }) => {
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (open) setForm(editing ? { ...EMPTY, ...editing, floor: editing.floor || "", total_floors: editing.total_floors ?? "", eids_code: editing.eids_code || "" } : { ...EMPTY, ...initial });
  }, [open, editing]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files).slice(0, 5 - form.image_paths.length);
    if (!files.length) return;
    setUploading(true);
    try {
      const ids = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        ids.push((await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data.id);
      }
      setForm((f) => ({ ...f, image_paths: [...f.image_paths, ...ids] }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price), area_m2: parseFloat(form.area_m2),
      total_floors: form.total_floors === "" ? null : parseInt(form.total_floors, 10),
      floor: form.floor || null, eids_code: form.has_eids ? form.eids_code : null,
    };
    try {
      if (editing) await api.put(`/portfolios/${editing.id}`, payload);
      else await api.post(createUrl, payload);
      toast.success(payload.status !== "ACTIVE" ? "Kaydedildi ve arşive taşındı" : editing ? "Portföy güncellendi" : "Portföy eklendi");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Portföy Düzenle" : "Yeni Portföy Ekle"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div><Label>İlan Başlığı *</Label><Input data-testid="portfolio-title-input" required value={form.title} onChange={(e) => set({ title: e.target.value })} /></div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label>İlan Tipi</Label>
              <Select value={form.listing_type} onValueChange={(v) => set({ listing_type: v })}>
                <SelectTrigger data-testid="portfolio-listing-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="SALE">Satılık</SelectItem><SelectItem value="RENT">Kiralık</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Daire Tipi</Label>
              <Select value={form.property_type} onValueChange={(v) => set({ property_type: v, rooms: v })}>
                <SelectTrigger data-testid="portfolio-property-type"><SelectValue /></SelectTrigger>
                <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => set({ status: v })}>
                <SelectTrigger data-testid="portfolio-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Fiyat (TL) *</Label><Input data-testid="portfolio-price-input" type="number" min="1" step="any" required value={form.price} onChange={(e) => set({ price: e.target.value })} /></div>
            <div><Label>Metrekare (m²) *</Label><Input data-testid="portfolio-area-input" type="number" min="0.01" step="any" required value={form.area_m2} onChange={(e) => set({ area_m2: e.target.value })} /></div>
            <div>
              <Label>Bulunduğu Kat</Label>
              <Select value={form.floor || undefined} onValueChange={(v) => set({ floor: v })}>
                <SelectTrigger data-testid="portfolio-floor-select"><SelectValue placeholder="Kat seçin" /></SelectTrigger>
                <SelectContent className="max-h-64">{FLOORS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Bina Kat Sayısı</Label><Input data-testid="portfolio-total-floors" type="number" min="0" value={form.total_floors} onChange={(e) => set({ total_floors: e.target.value })} /></div>
            <div><Label>İl *</Label><Input data-testid="portfolio-city-input" required value={form.city} onChange={(e) => set({ city: e.target.value })} /></div>
            <div><Label>İlçe *</Label><Input data-testid="portfolio-district-input" required value={form.district} onChange={(e) => set({ district: e.target.value })} /></div>
            <div className="col-span-2 md:col-span-3"><Label>Mahalle</Label><Input value={form.neighborhood || ""} onChange={(e) => set({ neighborhood: e.target.value })} /></div>
          </div>

          <div className="space-y-2"><Label>Cephe</Label><Chips testPrefix="facade" options={FACADES} value={form.facades} onChange={(v) => set({ facades: v })} /></div>
          <div className="space-y-2"><Label>Manzara</Label><Chips testPrefix="view" options={VIEWS} value={form.views} onChange={(v) => set({ views: v })} /></div>

          <div className="rounded-lg border border-slate-200 p-4 space-y-3">
            <label className="flex items-center justify-between">
              <span className="text-sm font-medium">EİDS (Elektronik İlan Doğrulama) onayı var mı?</span>
              <Switch data-testid="portfolio-eids-switch" checked={form.has_eids} onCheckedChange={(v) => set({ has_eids: v })} />
            </label>
            {form.has_eids && (
              <div><Label>EİDS Kodu *</Label><Input data-testid="portfolio-eids-code" required minLength={3} value={form.eids_code} onChange={(e) => set({ eids_code: e.target.value })} placeholder="EİDS doğrulama kodu" /></div>
            )}
          </div>

          {form.parcel && (
            <div data-testid="portfolio-parcel-badge" className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" />TKGM parseli bağlı: {form.parcel.mahalle} · Ada {form.parcel.ada} / Parsel {form.parcel.parsel}</span>
              <button type="button" data-testid="portfolio-parcel-unlink" className="text-red-600 hover:underline" onClick={() => set({ parcel: null })}>Bağlantıyı kaldır</button>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />Konum (haritaya tıklayarak işaretleyin)</Label>
              {form.lat != null && <button type="button" data-testid="clear-location-btn" className="text-xs text-red-600 hover:underline" onClick={() => set({ lat: null, lng: null })}>Konumu kaldır</button>}
            </div>
            <LocationPicker lat={form.lat} lng={form.lng} onChange={(lat, lng) => set({ lat, lng })} query={[form.neighborhood, form.district, form.city].filter((x) => x?.trim()).join(", ")} />
            <div data-testid="location-coords" className="text-xs text-slate-500 font-mono">{form.lat != null ? `${form.lat}, ${form.lng}` : "Konum işaretlenmedi"}</div>
          </div>

          <div><Label>Açıklama</Label><Textarea rows={3} value={form.description || ""} onChange={(e) => set({ description: e.target.value })} /></div>

          <div>
            <Label>Fotoğraflar ({form.image_paths.length}/5)</Label>
            <div className="grid grid-cols-5 gap-2 mt-2">
              {form.image_paths.map((id) => (
                <div key={id} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
                  <img src={fileUrl(id, "sm")} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => set({ image_paths: form.image_paths.filter((p) => p !== id) })} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                </div>
              ))}
              {form.image_paths.length < 5 && (
                <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : <><Upload className="w-5 h-5 text-slate-400" /><span className="text-[10px] text-slate-500 mt-1">Yükle</span></>}
                  <input data-testid="portfolio-image-upload" type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
                </label>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>İptal</Button>
            <Button data-testid="portfolio-save-btn" type="submit" className="bg-emerald-600 hover:bg-emerald-700">{editing ? "Güncelle" : "Kaydet"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
